import { createRef, useCallback, useEffect, useState } from 'react';
import LeasingNavigation from '../../components/navigation.jsx';
import useLeasingNavigation from '../../../../hooks/useLeasingNavigation.jsx';
import { LeasingSteps } from '../../leasing-consts.js';
import RequisitionLayout from '../../components/requisition-layout/requisition-layout.jsx';
import AreqSidebar from '../../components/areq-sidebar/areq-sidebar.jsx';
import { useSelector } from 'react-redux';
import AreqDetailsContact from './components/areq-details-contact/areq-details-contact.jsx';
import './areq-details.scss';
import { Button, Spinner } from '@gsa/afp-component-library';
import RequirementForm from './components/additional-requirement/additional-requirement.jsx';
import { setAdditionalRequirements } from '../../../../reducers/leasing.jsx';
import { useDispatch } from 'react-redux';
import { useGetAttachmentTypes } from '../../../../requests/leasing.jsx';
import { useCreateAdditionalRequirment, useDeleteAdditionalRequirement, useGetEngineerPocs } from '../../../../requests/additionals-request.jsx';
import { applyFormErrorsRecursively } from '@gsa/afp-shared-form-utils';
import useUpdateDraft from '../../components/update-draft/update-leasing-draft.js';
import { formatCurrency } from '../../../../utilities/CompareVehicleUtils.jsx';
import ReminderModal from './components/reminder-modal/reminder-modal.jsx';
import { useModal } from '@gsa/afp-component-library';

const AREQAttachments = {
  req_additional_requirement: 'Requisition Additional Requirement',
  req_igce: 'Requisition IGCE Documentation',
};

const AreqDetails = () => {
  const { handleLeasingNavigation } = useLeasingNavigation();
  const dispatch = useDispatch();
  const { isOpen, openModal, closeModal } = useModal();
  const createdByUser = useSelector((state) => state.leasingReducer.createdByUserInfo);
  const requisitionId = useSelector((state) => state.leasingReducer.requisitionId);
  const selectedStandardItem = useSelector((state) => state.leasingReducer.selectedStandardItem);
  const additionalRequirements = useSelector((state) => state.leasingReducer.additionalRequirements);
  const isPaintAndGraphicsDisabled = useSelector((state) => state.leasingReducer.isPaintAndGraphicsDisabled);
  const allAttachmentTypes = useSelector((state) => state.leasingReducer.attachmentTypes);
  const vehicleTypeCode = useSelector((state) => state.leasingReducer.selectedStandardItem?.vehicleTypeCode?.parentCode);
  const [createAdditionalRequirement, { loading: createAdditionalRequirementLoading }] = useCreateAdditionalRequirment();
  const [deleteAdditionalRequirement, { loading: deleteAdditionalRequirementLoading }] = useDeleteAdditionalRequirement();
  const { data: engineers, loading: loadingEngineers } = useGetEngineerPocs({ vehicleTypeCode });
  const [updateDraft] = useUpdateDraft();
  useGetAttachmentTypes({});
  const [formRefs, setFormRefs] = useState([]);
  const priceEstimates = additionalRequirements?.reduce((acc, requirement, index) => {
    acc[index] = requirement.priceEstimate || 0;
    return acc;
  }, {});
  const totalPriceEstimate = Object.values(priceEstimates).reduce((acc, price) => acc + (price || 0), 0);

  const attachmentTypes = !allAttachmentTypes ? [] : allAttachmentTypes.filter((type) => {
    return Object.keys(AREQAttachments).includes(type.attachmentType);
  }).map((type) => ({
    value: type.requisitionAttachmentTypeId,
    label: AREQAttachments[type.attachmentType],
  }));

  const openReminderModal = () => {
    const ignoreReminder = sessionStorage.getItem('ignoreAreqReminderModal');
    if (!ignoreReminder) {
      openModal();
    }
  }

  const addAdditionalRequirement = async () => {
    try {
      const additionalRequirement = await createAdditionalRequirement({
        variables: {
          data: {
            requisitionId
          }
        }
      });

      dispatch(setAdditionalRequirements([...additionalRequirements, additionalRequirement.data]));
      setFormRefs((prevRefs) => {
        const newRefs = [...prevRefs];
        newRefs.push(createRef());
        return newRefs;
      });

      openReminderModal();
    } catch (error) {
      console.error('Error creating additional requirement:', error);
    }
  }

  const handleRemoveAdditionalRequirement = useCallback(async (id) => {
    try {
      const { data: deleted } = await deleteAdditionalRequirement({
        variables: {
          additionalRequirementId: id
        }
      });

      if (deleted) {
        const updatedRequirements = additionalRequirements.filter((requirement) => requirement.requisitionAdditionalRequirementId !== id);
        dispatch(setAdditionalRequirements(updatedRequirements));
        setFormRefs((prevRefs) => {
          const newRefs = [...prevRefs];
          const indexToRemove = additionalRequirements.findIndex((requirement) => requirement.requisitionAdditionalRequirementId === id);
          if (indexToRemove !== -1) {
            newRefs.splice(indexToRemove, 1);
          }
          return newRefs;
        });
      }
    } catch (error) {
      console.error('Error removing additional requirement:', error);
    }
  }, [additionalRequirements]);

  useEffect(() => {
    openReminderModal();
    if (!additionalRequirements) return;
    if (additionalRequirements.length === 0) {
      addAdditionalRequirement();
    }
  }, []);

  useEffect(() => {
    setFormRefs((prevRefs) => {
      const newRefs = [...prevRefs];
      additionalRequirements.forEach((requirement, index) => {
        if (!newRefs[index]) {
          newRefs[index] = createRef();
        }
      });
      return newRefs;
    });
  }, [additionalRequirements]);

  const handleSubmit = async () => {
    if (formRefs.length === 0) {
      return;
    }

    const additionalRequirementsData = [];
    let hasErrors = false;

    for (let i = 0; i < formRefs.length; i++) {
      const formRef = formRefs[i];
      const { values, errors } = await formRef.current.getValidatedValues(true);
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        for (const key of errorKeys) {
          applyFormErrorsRecursively(key, errors, formRef.current.setError, formRef.current.clearErrors);
        }

        hasErrors = true;
        continue;
      }

      additionalRequirementsData.push({
        requisitionAdditionalRequirementId: values[`id-${i}`],
        description: values[`description-${i}`],
        priceEstimate: values[`priceEstimate-${i}`],
        attachments: (values[`files-${i}`] || []).map((attachment) => ({
          requisitionAttachmentId: attachment.id,
          name: attachment.name,
          attachmentTypeId: attachment.category,
          description: attachment.note,
          signedUrl: attachment.signedUrl,
          metadataId: attachment.metadataId,
        })),
      })
    };

    if (hasErrors) return null;

    const input = {
      additionalRequirements: additionalRequirementsData
    }

    let nextStep = !isPaintAndGraphicsDisabled ? LeasingSteps.PAINT_AND_GRAPHICS : LeasingSteps.REVIEW_VEHICLE_BUILD;

    updateDraft(input, nextStep).then((data) => {
      if (data) {
        dispatch(setAdditionalRequirements(additionalRequirementsData.map((requirement) => ({
          ...requirement,
          requisitionId: requisitionId,
        }))));
        handleLeasingNavigation(nextStep);
      }
    })
  }

  if (loadingEngineers) {
    return <Spinner />;
  }

  return (
    <RequisitionLayout
      left={
        <AreqSidebar standardItem={selectedStandardItem} />
      }

      right={
        <div className='areq-details'>
          <div>
            <strong>Provide information for additional requirements</strong>
            <p style={{ margin: 0 }}>
              Please supply all required information following the instructions in the panel on the left.
            </p>
          </div>

          <div className='areq-details-contact-container'>
            <AreqDetailsContact
              title='AREQ preparer'
              contact={{
                name: [createdByUser.firstName, createdByUser.lastName].filter(Boolean).join(' '),
                email: createdByUser.email,
                phone: {
                  country: createdByUser.phoneNumberCountryCode,
                  number: createdByUser.phoneNumber,
                  extension: createdByUser.phoneNumberExt,
                }
              }}
            />
            <AreqDetailsContact
              title='Assigned engineer'
              contact={{
                name: engineers?.primary?.name || '',
                email: engineers?.primary?.email || '',
                phone: {
                  country: '',
                  number: engineers?.primary?.phoneNumber || '',
                  extension: '',
                }
              }}
            />
            <AreqDetailsContact
              title='Secondary engineer contact'
              contact={{
                name: engineers?.secondary?.name || '',
                email: engineers?.secondary?.email || '',
                phone: {
                  country: '',
                  number: engineers?.secondary?.phoneNumber || '',
                  extension: '',
                }
              }}
            />
          </div>

          <div className='areq-details-additionals-container'>
            <div>
              <h3 className='blue-header'>List Additional requirements and price estimate</h3>
              <p>Provide line items for each requirement (with optional supporting documentation). Please note that the vehicle will be built in accordance with the accepted contractor’s response to the AREQ,  and not the customer’s AREQ.  As such, it is GSA’s responsibility to ensure the contractor’s response meets the customer agency’s needs at the time of selection.</p>
            </div>

            <div className='areq-details-additional-requirement-container'>
              <div className='add-requirement-action'>
                <Button
                  leftIcon={{ name: 'add' }}
                  label='Add requirement'
                  onClick={addAdditionalRequirement}
                  disabled={createAdditionalRequirementLoading}
                />
              </div>

              <div className='areq-details-additional-requirements'>
                {additionalRequirements.map((requirement, index) => {
                  if (!formRefs[index]) {
                    return null;
                  }

                  return (
                    <div key={requirement.requisitionAdditionalRequirementId + index} className='areq-details-additional-requirement-form-container'>
                      <div className='areq-details-additional-requirement-form-header'>
                        <h3>Additional requirement {index + 1}</h3>
                        {index > 0 && (
                          <div>
                            <Button
                              variant='unstyled'
                              label='Delete this requirement'
                              onClick={() => handleRemoveAdditionalRequirement(requirement.requisitionAdditionalRequirementId)}
                              disabled={deleteAdditionalRequirementLoading}
                            />
                          </div>
                        )}
                      </div>
                      <RequirementForm
                        requirement={requirement}
                        attachmentTypes={attachmentTypes}
                        index={index}
                        ref={formRefs[index]}
                      />
                    </div>
                  )
                })}
              </div>


              <div className="total-req-amount-div">
                <div className="total-req-description">
                  Total aggregate price estimate of AREQ from Independent Government Cost Estimate (IGCE)
                </div>
                <div className="total-req-amount">{formatCurrency(totalPriceEstimate)}</div>
              </div>
            </div>
          </div>

          <LeasingNavigation
            onPrevious={() => {
              handleLeasingNavigation(LeasingSteps.COMPARE_AND_SELECT);
            }}
            onNext={handleSubmit}
            hide={['cancel', 'submit', 'reject']}
          />

          <ReminderModal
            isOpen={isOpen}
            closeModal={closeModal}
          />
        </div>
      }
    />
  );
}

export default AreqDetails;